import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SiteLayout } from "@/components/layout/SiteLayout";
import { Section, SectionLabel } from "@/components/layout/Section";
import { Reveal } from "@/lib/motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "sonner";
import { createDonationSession } from "@/lib/api";
import { cn } from "@/lib/utils";
import { DONATE } from "@/constants/testIds";
import { Loader2, Lock, HeartHandshake, XCircle } from "lucide-react";

// Preset tiers (mirrors backend DONATION_TIERS)
export const DONATION_TIERS = [
  { id: "t25", label: "$25", amount_cents: 2500 },
  { id: "t50", label: "$50", amount_cents: 5000 },
  { id: "t100", label: "$100", amount_cents: 10000 },
  { id: "t1000", label: "$1,000", amount_cents: 100000 },
];

const MIN_CUSTOM = 5;

export default function DonatePage() {
  const [searchParams] = useSearchParams();
  const cancelled = searchParams.get("cancelled") === "true";

  const [cadence, setCadence] = useState("one_time");
  const [selectedTier, setSelectedTier] = useState(DONATION_TIERS[1].id); // default $50
  const [customAmount, setCustomAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const usingCustom = customAmount.trim() !== "";
  const customDollars = Number(customAmount);
  const customValid = usingCustom && Number.isFinite(customDollars) && customDollars >= MIN_CUSTOM;

  const displayAmount = useMemo(() => {
    if (usingCustom) return customValid ? `$${customDollars.toLocaleString(undefined, { minimumFractionDigits: 0 })}` : "—";
    const tier = DONATION_TIERS.find((t) => t.id === selectedTier);
    return tier ? tier.label : "—";
  }, [usingCustom, customValid, customDollars, selectedTier]);

  function handleTierClick(id) {
    setSelectedTier(id);
    setCustomAmount("");
    setError(null);
  }

  async function handleSubmit() {
    setError(null);
    let payload = {
      cadence,
      origin_url: window.location.origin,
    };
    if (usingCustom) {
      if (!customValid) {
        setError("Please enter an amount of at least $5.");
        return;
      }
      payload.amount_cents = Math.round(customDollars * 100);
    } else {
      payload.tier_id = selectedTier;
    }
    setSubmitting(true);
    try {
      const { session_url } = await createDonationSession(payload);
      if (!session_url) throw new Error("Missing session URL");
      window.location.href = session_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message || "Something went wrong. Please try again.";
      setError(msg);
      toast.error(msg);
      setSubmitting(false);
    }
  }

  return (
    <SiteLayout>
      <Toaster theme="dark" position="top-center" richColors closeButton />

      <section className="relative overflow-hidden">
        <div aria-hidden className="absolute inset-0 brp-hero-glow" />
        <div aria-hidden className="absolute inset-0 brp-hero-glow-orange opacity-70" />
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-10 pt-16 pb-8 sm:pt-24">
          <Reveal>
            <SectionLabel tone="orange">Legacy League — Support the Mission</SectionLabel>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-6 font-display font-semibold tracking-[-0.03em] text-4xl sm:text-5xl lg:text-6xl leading-[1.02] max-w-3xl">
              Invest in the next generation of founders.
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 max-w-2xl text-[hsl(var(--brp-ink-dim))] leading-relaxed">
              Your support helps expand the technology, learning experiences, software, AI infrastructure, and founder development resources that power Legacy League. Every contribution helps create greater access to entrepreneurship and opportunity for future innovators.
            </p>
          </Reveal>
        </div>
      </section>

      <Section tone="dark" py="md">
        {cancelled && (
          <Reveal>
            <div
              role="status"
              data-testid="donate-cancelled-banner"
              className="mb-8 rounded-xl border border-[hsl(var(--brp-orange))]/40 bg-[hsl(var(--brp-orange))]/10 px-4 py-3 flex items-start gap-3"
            >
              <XCircle className="h-5 w-5 text-[hsl(var(--brp-orange))] mt-0.5" />
              <div className="text-sm">
                <div className="text-[hsl(var(--brp-ink))] font-medium">Your donation was not completed.</div>
                <div className="text-[hsl(var(--brp-ink-dim))] mt-0.5">No charges were made. You can try again below whenever you’re ready.</div>
              </div>
            </div>
          </Reveal>
        )}

        <Reveal>
          <div className="rounded-2xl border border-border/70 bg-[hsl(var(--brp-surface-2))] p-6 sm:p-10 shadow-brp">
            {/* Cadence toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div>
                <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-blue))]">Donation Cadence</div>
                <p className="mt-1 text-sm text-[hsl(var(--brp-ink-dim))]">Choose how you’d like to support Legacy League.</p>
              </div>
              <div
                role="tablist"
                aria-label="Donation cadence"
                className="inline-flex rounded-full border border-border/70 bg-[hsl(var(--brp-surface-3))] p-1"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={cadence === "one_time"}
                  data-testid={DONATE.cadenceOneTime}
                  onClick={() => setCadence("one_time")}
                  className={cn(
                    "px-5 h-10 rounded-full text-sm font-medium tracking-wide transition-colors brp-focus",
                    cadence === "one_time"
                      ? "bg-[hsl(var(--brp-blue))] text-white"
                      : "text-[hsl(var(--brp-ink-dim))] hover:text-[hsl(var(--brp-ink))]"
                  )}
                >
                  One-Time
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={cadence === "monthly"}
                  data-testid={DONATE.cadenceMonthly}
                  onClick={() => setCadence("monthly")}
                  className={cn(
                    "px-5 h-10 rounded-full text-sm font-medium tracking-wide transition-colors brp-focus",
                    cadence === "monthly"
                      ? "bg-[hsl(var(--brp-blue))] text-white"
                      : "text-[hsl(var(--brp-ink-dim))] hover:text-[hsl(var(--brp-ink))]"
                  )}
                >
                  Monthly
                </button>
              </div>
            </div>

            {/* Preset tiers */}
            <div className="mt-10">
              <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-blue))]">Choose an Amount</div>
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                {DONATION_TIERS.map((t) => {
                  const isSelected = !usingCustom && selectedTier === t.id;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      data-testid={DONATE.tierButton}
                      data-tier={t.id}
                      onClick={() => handleTierClick(t.id)}
                      className={cn(
                        "h-14 rounded-xl border text-lg font-display font-semibold tracking-tight brp-focus transition-colors",
                        isSelected
                          ? "border-[hsl(var(--brp-blue))] bg-[hsl(var(--brp-blue))]/10 text-white"
                          : "border-border/70 bg-[hsl(var(--brp-surface-3))] text-[hsl(var(--brp-ink))] hover:border-[hsl(var(--brp-blue))]/40"
                      )}
                    >
                      {t.label}{cadence === "monthly" ? <span className="text-xs font-normal text-[hsl(var(--brp-ink-dim))] ml-1">/mo</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom amount */}
            <div className="mt-8">
              <Label htmlFor="custom-amount" className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-blue))]">Or Enter a Custom Amount</Label>
              <div className="mt-3 flex items-center gap-3 max-w-sm">
                <div className="relative flex-1">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[hsl(var(--brp-ink-dim))]">$</span>
                  <Input
                    id="custom-amount"
                    type="number"
                    min={MIN_CUSTOM}
                    step="1"
                    inputMode="numeric"
                    data-testid={DONATE.customInput}
                    value={customAmount}
                    onChange={(e) => { setCustomAmount(e.target.value); setError(null); }}
                    placeholder={`Minimum $${MIN_CUSTOM}`}
                    className="pl-8 h-12 bg-[hsl(var(--brp-surface-3))] border-border/70"
                  />
                </div>
                {cadence === "monthly" && <span className="text-sm text-[hsl(var(--brp-ink-dim))]">/ month</span>}
              </div>
              <p className="mt-2 text-xs text-[hsl(var(--brp-ink-dim))]">Minimum donation: ${MIN_CUSTOM}</p>
              {error && <p data-testid="donate-error" className="mt-3 text-sm text-red-400">{error}</p>}
            </div>

            {/* Summary + CTA */}
            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-6 border-t border-border/70">
              <div>
                <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-ink-dim))]">Your Contribution</div>
                <div className="mt-1 font-display text-3xl font-semibold">
                  {displayAmount}
                  {cadence === "monthly" && displayAmount !== "—" && <span className="text-base font-normal text-[hsl(var(--brp-ink-dim))]"> / month</span>}
                </div>
              </div>
              <Button
                onClick={handleSubmit}
                disabled={submitting || (usingCustom && !customValid)}
                data-testid={DONATE.submit}
                className="h-12 sm:min-w-[280px] rounded-full bg-gradient-to-r from-[hsl(var(--brp-blue))] to-[hsl(var(--brp-orange))] text-white font-semibold tracking-[0.18em] uppercase hover:opacity-95"
              >
                {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Redirecting…</>) : (
                  <><HeartHandshake className="mr-2 h-4 w-4" /> Support the Mission</>
                )}
              </Button>
            </div>
            <div className="mt-4 flex items-center justify-center sm:justify-end gap-2 text-xs text-[hsl(var(--brp-ink-dim))]">
              <Lock className="h-3.5 w-3.5" /> Secure payment powered by Stripe.
            </div>
          </div>
        </Reveal>
      </Section>

      <Section tone="paper" py="md">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-[hsl(var(--brp-orange))]">Where Your Support Goes</div>
            <h2 className="mt-4 font-display font-semibold tracking-tight text-2xl sm:text-3xl leading-tight">
              Technology, learning, and opportunity — delivered with intention.
            </h2>
          </div>
          <p className="text-[hsl(var(--brp-ink-muted-on-paper))] leading-relaxed">
            Every contribution helps expand the software, AI infrastructure, curriculum, and mentorship experiences that make Legacy League possible. Your generosity directly powers the next generation of founders.
          </p>
        </div>
        <div className="mt-10 flex items-center justify-center">
          <Link to="/legacy-league" className="text-sm tracking-wide uppercase text-[hsl(var(--brp-blue))] hover:underline">← Back to Legacy League</Link>
        </div>
      </Section>
    </SiteLayout>
  );
}
